import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, TextField, Button, Chip, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Select,
  MenuItem, FormControl, InputLabel, Alert, CircularProgress,
  InputAdornment, Divider, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Search, Plus, Edit2, Trash2, KeyRound, Users,
  PhoneCall, UserCog, RefreshCw,
} from "lucide-react";
import { useColorMode } from "../../../theme/ColorModeContext";

import axiosInstance from "../../../services/axiosInstance";
import PasswordRequirements from "../../common/PasswordRequirements";
import { isPasswordValid } from "../../../utils/passwordPolicy";
import { useTranslation } from "react-i18next";

const PLANS  = ["Starter", "Plan A", "Plan B", "Enterprise", "-"];
const ROLES  = ["User", "admin", "-"];

function RoleBadge({ role }) {
  const color = role === "admin" ? "#DC2626" : role === "User" ? "#4F46E5" : "#94A3B8";
  return (
    <Chip
      label={role || "-"}
      size="small"
      sx={{ height: 20, fontSize: "0.6875rem", fontWeight: 700, background: alpha(color, 0.1), color, border: "none" }}
    />
  );
}

function PlanBadge({ plan }) {
  const color = plan === "Enterprise" ? "#F59E0B" : plan?.startsWith("Plan") ? "#4F46E5" : "#94A3B8";
  return (
    <Chip label={plan || "-"} size="small" sx={{ height: 20, fontSize: "0.6875rem", fontWeight: 600, background: alpha(color, 0.1), color, border: "none" }} />
  );
}

const EMPTY_EDIT = { id: null, firstName: "", lastName: "", plan: "", role: "", mobileNumber: "", limit: 0 };
const EMPTY_PWD  = { id: null, name: "", newPassword: "", confirmPassword: "" };

const UserManagement = () => {
  const { t }    = useTranslation("admin");
  const { mode } = useColorMode();
  const isLight  = mode === "light";

  const [users,   setUsers]   = useState([]);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(0);
  const [rowsPerPage]         = useState(15);
  const [q,       setQ]       = useState("");
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");
  const [success, setSuccess] = useState("");

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(EMPTY_EDIT);
  const [editSaving, setEditSaving] = useState(false);
  const [editError,  setEditError]  = useState("");

  const [pwdOpen,  setPwdOpen]  = useState(false);
  const [pwdForm,  setPwdForm]  = useState(EMPTY_PWD);
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdError,  setPwdError]  = useState("");

  const [deleteId,  setDeleteId]  = useState(null);
  const [deleting,  setDeleting]  = useState(false);

  const glassSx = { background: "var(--surface-card)", border: "1px solid var(--line-200)", boxShadow: "var(--shadow-card)", borderRadius: "18px", overflow: "hidden" };

  const load = useCallback(async (pg = page, query = q) => {
    setLoading(true); setError("");
    try {
      const r = await axiosInstance.get("/admin/users", { params: { q: query, page: pg + 1, size: rowsPerPage } });
      setUsers(r.data.users);
      setTotal(r.data.total);
    } catch (e) { setError(e.response?.data?.message || t("failedLoadUsers")); }
    finally { setLoading(false); }
  }, [page, q, rowsPerPage]);

  useEffect(() => { load(); }, [page]); // eslint-disable-line

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    load(0, q);
  };

  // Edit
  const openEdit = (u) => {
    setEditForm({ id: u.id, firstName: u.firstName || "", lastName: u.lastName || "", plan: u.plan || "", role: u.role || "", mobileNumber: u.mobileNumber || "", limit: u.limit || 0 });
    setEditError(""); setEditOpen(true);
  };
  const saveEdit = async () => {
    setEditSaving(true); setEditError("");
    try {
      const r = await axiosInstance.put(`/admin/users/${editForm.id}`, {
        firstName: editForm.firstName, lastName: editForm.lastName,
        plan: editForm.plan, role: editForm.role,
        mobileNumber: editForm.mobileNumber, limit: Number(editForm.limit),
      });
      setUsers(prev => prev.map(u => u.id === r.data.id ? { ...u, ...r.data } : u));
      setSuccess(t("userUpdated")); setEditOpen(false);
      setTimeout(() => setSuccess(""), 3000);
    } catch (e) { setEditError(e.response?.data?.message || t("failedSaveUser")); }
    finally { setEditSaving(false); }
  };

  // Password reset
  const openPwd = (u) => { setPwdForm({ id: u.id, name: u.name, newPassword: "", confirmPassword: "" }); setPwdError(""); setPwdOpen(true); };
  const savePwd = async () => {
    if (!isPasswordValid(pwdForm.newPassword)) { setPwdError(t("passwordDoesNotMeet")); return; }
    if (pwdForm.newPassword !== pwdForm.confirmPassword) { setPwdError(t("passwordsDoNotMatch")); return; }
    setPwdSaving(true); setPwdError("");
    try {
      await axiosInstance.post(`/admin/users/${pwdForm.id}/reset-password`, { newPassword: pwdForm.newPassword });
      setSuccess(t("passwordReset")); setPwdOpen(false);
      setTimeout(() => setSuccess(""), 3000);
    } catch (e) { setPwdError(e.response?.data?.message || t("failedResetPassword")); }
    finally { setPwdSaving(false); }
  };

  // Delete
  const confirmDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await axiosInstance.delete(`/admin/users/${deleteId}`);
      setUsers(prev => prev.filter(u => u.id !== deleteId));
      setTotal(prev => prev - 1);
      setSuccess(t("userDeleted")); setDeleteId(null);
      setTimeout(() => setSuccess(""), 3000);
    } catch (e) { setError(e.response?.data?.message || t("failedDeleteUser")); setDeleteId(null); }
    finally { setDeleting(false); }
  };

  const headCell = { fontWeight: 700, fontSize: "0.75rem", color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.06em", py: 1.5, borderBottom: "1px solid var(--line-200)", whiteSpace: "nowrap" };
  const bodyCell = { fontSize: "0.875rem", py: 1.25, borderBottom: "1px solid var(--line-100)" };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: "-0.02em", color: "text.primary", mb: 0.5 }}>
            {t("userMgmtTitle")}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {t("totalUsersCount", { count: total })}
          </Typography>
        </Box>
        <Button variant="outlined" size="small" startIcon={<RefreshCw size={14} />} onClick={() => load()} sx={{ borderRadius: 2, fontWeight: 600, textTransform: "none" }}>
          {t("refresh")}
        </Button>
      </Box>

      {error   && <Alert severity="error"   sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError("")}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess("")}>{success}</Alert>}

      {/* Search bar */}
      <Box component="form" onSubmit={handleSearch} sx={{ mb: 2.5, display: "flex", gap: 1.5 }}>
        <TextField
          value={q} onChange={e => setQ(e.target.value)}
          placeholder={t("searchByNameEmail")}
          size="small" sx={{ flex: 1 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search size={16} /></InputAdornment> }}
        />
        <Button type="submit" variant="contained" size="small" sx={{ background: "var(--grad-brand-135)", borderRadius: 2, fontWeight: 600, textTransform: "none", px: 2 }}>
          {t("search")}
        </Button>
      </Box>

      {/* Table */}
      <Box sx={glassSx}>
        {loading && <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}><CircularProgress size={28} sx={{ color: "#4F46E5" }} /></Box>}
        {!loading && (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: isLight ? alpha("#4F46E5", 0.03) : alpha("#6366F1", 0.06) }}>
                  <TableCell sx={headCell}>{t("colId")}</TableCell>
                  <TableCell sx={headCell}>{t("colName")}</TableCell>
                  <TableCell sx={headCell}>{t("colEmail")}</TableCell>
                  <TableCell sx={headCell}>{t("colPlan")}</TableCell>
                  <TableCell sx={headCell}>{t("colRole")}</TableCell>
                  <TableCell sx={{ ...headCell, textAlign: "right" }}>{t("colCalls")}</TableCell>
                  <TableCell sx={{ ...headCell, textAlign: "right" }}>{t("colSubAcc")}</TableCell>
                  <TableCell sx={{ ...headCell, textAlign: "center" }}>{t("colActions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ textAlign: "center", py: 5, color: "text.disabled" }}>
                      <Users size={32} style={{ opacity: 0.3, marginBottom: 8, display: "block", margin: "0 auto 8px" }} />
                      {t("noUsersFound")}
                    </TableCell>
                  </TableRow>
                ) : users.map(u => (
                  <TableRow key={u.id} sx={{ "&:hover": { background: isLight ? alpha("#4F46E5", 0.02) : alpha("#6366F1", 0.05) }, transition: "background 0.1s" }}>
                    <TableCell sx={{ ...bodyCell, color: "text.disabled", fontFamily: "monospace" }}>#{u.id}</TableCell>
                    <TableCell sx={bodyCell}>
                      <Box>
                        <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: "text.primary" }}>
                          {u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.name}
                        </Typography>
                        {u.enCompanyName && <Typography variant="caption" sx={{ color: "text.disabled" }}>{u.enCompanyName}</Typography>}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ ...bodyCell, color: "text.secondary" }}>{u.email}</TableCell>
                    <TableCell sx={bodyCell}><PlanBadge plan={u.plan} /></TableCell>
                    <TableCell sx={bodyCell}><RoleBadge role={u.role} /></TableCell>
                    <TableCell sx={{ ...bodyCell, textAlign: "right", fontVariantNumeric: "tabular-nums", color: "text.secondary" }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
                        <PhoneCall size={12} />
                        {u.totalCalls.toLocaleString()}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ ...bodyCell, textAlign: "right", fontVariantNumeric: "tabular-nums", color: "text.secondary" }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
                        <UserCog size={12} />
                        {u.subAccountCount}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ ...bodyCell, textAlign: "center" }}>
                      <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
                        <Tooltip title={t("tooltipEditUser")} arrow>
                          <IconButton size="small" onClick={() => openEdit(u)} sx={{ color: "#4F46E5", "&:hover": { background: alpha("#4F46E5", 0.1) } }}>
                            <Edit2 size={15} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("tooltipResetPassword")} arrow>
                          <IconButton size="small" onClick={() => openPwd(u)} sx={{ color: "#F59E0B", "&:hover": { background: alpha("#F59E0B", 0.1) } }}>
                            <KeyRound size={15} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("tooltipDeleteUser")} arrow>
                          <IconButton size="small" onClick={() => setDeleteId(u.id)} sx={{ color: "#EF4444", "&:hover": { background: alpha("#EF4444", 0.1) } }}>
                            <Trash2 size={15} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
        <TablePagination
          component="div" count={total} page={page} onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage} rowsPerPageOptions={[15]}
          sx={{ borderTop: "1px solid var(--line-200)", "& .MuiTablePagination-toolbar": { minHeight: 48 } }}
        />
      </Box>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>{t("editUserTitle")}</DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          {editError && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{editError}</Alert>}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField label={t("labelFirstName")} fullWidth size="small" value={editForm.firstName} onChange={e => setEditForm(f => ({ ...f, firstName: e.target.value }))} />
              <TextField label={t("labelLastName")}  fullWidth size="small" value={editForm.lastName}  onChange={e => setEditForm(f => ({ ...f, lastName:  e.target.value }))} />
            </Box>
            <TextField label={t("labelMobile")} fullWidth size="small" value={editForm.mobileNumber} onChange={e => setEditForm(f => ({ ...f, mobileNumber: e.target.value }))} />
            <Box sx={{ display: "flex", gap: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t("colPlan")}</InputLabel>
                <Select label={t("colPlan")} value={editForm.plan} onChange={e => setEditForm(f => ({ ...f, plan: e.target.value }))}>
                  {PLANS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>{t("colRole")}</InputLabel>
                <Select label={t("colRole")} value={editForm.role} onChange={e => setEditForm(f => ({ ...f, role: e.target.value }))}>
                  {ROLES.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
            <TextField label={t("labelSubAccountLimit")} type="number" fullWidth size="small" value={editForm.limit} onChange={e => setEditForm(f => ({ ...f, limit: e.target.value }))} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setEditOpen(false)} sx={{ color: "text.secondary", textTransform: "none" }}>{t("cancel")}</Button>
          <Button variant="contained" onClick={saveEdit} disabled={editSaving}
            startIcon={editSaving ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{ background: "var(--grad-brand-135)", borderRadius: 2, textTransform: "none", fontWeight: 600 }}>
            {editSaving ? t("saving") : t("saveChanges")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={pwdOpen} onClose={() => setPwdOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>{t("resetPasswordTitle", { name: pwdForm.name })}</DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          {pwdError && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{pwdError}</Alert>}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <Box>
              <TextField label={t("labelNewPassword")} type="password" fullWidth size="small" value={pwdForm.newPassword}
                onChange={e => setPwdForm(f => ({ ...f, newPassword: e.target.value }))} />
              <PasswordRequirements password={pwdForm.newPassword} />
            </Box>
            <TextField label={t("labelConfirmPassword")} type="password" fullWidth size="small" value={pwdForm.confirmPassword}
              onChange={e => setPwdForm(f => ({ ...f, confirmPassword: e.target.value }))}
              onKeyDown={e => e.key === "Enter" && savePwd()} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setPwdOpen(false)} sx={{ color: "text.secondary", textTransform: "none" }}>{t("cancel")}</Button>
          <Button variant="contained" onClick={savePwd} disabled={pwdSaving}
            startIcon={pwdSaving ? <CircularProgress size={14} color="inherit" /> : <KeyRound size={14} />}
            sx={{ background: "linear-gradient(135deg,#F59E0B,#EF4444)", borderRadius: 2, textTransform: "none", fontWeight: 600 }}>
            {pwdSaving ? t("resetting") : t("resetPassword")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={Boolean(deleteId)} onClose={() => setDeleteId(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>{t("deleteUserTitle")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {t("deleteUserConfirm")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeleteId(null)} sx={{ color: "text.secondary", textTransform: "none" }}>{t("cancel")}</Button>
          <Button variant="contained" onClick={confirmDelete} disabled={deleting}
            startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <Trash2 size={14} />}
            sx={{ background: "#DC2626", borderRadius: 2, textTransform: "none", fontWeight: 600, "&:hover": { background: "#B91C1C" } }}>
            {deleting ? t("deleting") : t("delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
